import { useState, type ReactNode } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { colors } from '@/theme/tokens';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  // Optional icon rendered to the left of the title. Color defaults to
  // brand orange in destructive mode, inherits otherwise.
  icon?: ReactNode;
  // Body text / ReactNode shown below the title. Use a string for the
  // common case; pass ReactNode if you need custom layout.
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  // Styles the confirm button with the brand orange destructive look
  // (matches the existing Delete Device dialog in device-detail). When
  // false the confirm button uses MUI's default primary color.
  destructive?: boolean;
  // External loading state — while true, both buttons are disabled,
  // the confirm button shows a spinner + loadingLabel, and backdrop /
  // escape dismissal is blocked so the user sees the request through.
  loading?: boolean;
  loadingLabel?: string;
  // Error message to surface inside the dialog (e.g. when the confirm
  // action's mutation rejected). The dialog stays open so the user can
  // read the error and retry.
  error?: ReactNode;
  // When true, the confirm button is hidden once `error` is set, leaving
  // only Cancel. Use for confirmations whose failure is not worth retrying
  // in place (e.g. deleting a group that's in use by a rollout) — the user
  // reads the error and closes. Default false keeps the button visible.
  hideConfirmOnError?: boolean;
  // When set, the confirm button stays disabled until the user types this
  // exact word (case-insensitive, trimmed) into a text field. Use for
  // irreversible / high-blast-radius actions where a single click is too
  // easy to fire by accident.
  requireTypedConfirmation?: string;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  icon,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  loadingLabel,
  error,
  hideConfirmOnError = false,
  requireTypedConfirmation,
}: ConfirmDialogProps) {
  const confirmSx = destructive
    ? { bgcolor: colors.primary.main, '&:hover': { bgcolor: colors.primary.hover } }
    : undefined;

  const [typed, setTyped] = useState('');
  // The typed value is reset on the Dialog's close transition (onExited below)
  // so reopening always starts from an empty field / disabled confirm button.

  const typedMatches =
    !requireTypedConfirmation ||
    typed.trim().toLowerCase() === requireTypedConfirmation.trim().toLowerCase();
  const confirmDisabled = loading || !typedMatches;
  const hideConfirm = hideConfirmOnError && !!error;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{ transition: { onExited: () => setTyped('') } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {icon}
        {title}
      </DialogTitle>
      <DialogContent>
        {description && (
          typeof description === 'string'
            ? <DialogContentText>{description}</DialogContentText>
            : description
        )}
        {requireTypedConfirmation && (
          <TextField
            autoFocus
            fullWidth
            size="small"
            margin="dense"
            value={typed}
            disabled={loading}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && typedMatches && !loading) onConfirm();
            }}
            placeholder={requireTypedConfirmation}
            label={`Type "${requireTypedConfirmation}" to confirm`}
            sx={{ mt: 2 }}
          />
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading} sx={{ color: '#161616' }}>
          {cancelLabel}
        </Button>
        {!hideConfirm && (
          <Button
            onClick={onConfirm}
            variant="contained"
            disabled={confirmDisabled}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={confirmSx}
          >
            {loading && loadingLabel ? loadingLabel : confirmLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
